import React, { useState } from 'react'
import { localizedStrings } from '@core/strings'
import { Box, Text } from '@fundamentals'
import { Autocomplete } from '@common/material/Autocomplete'
import { TextField } from '@common/material'
import { TextFieldProps } from '@mui/material/TextField/TextField'
import { useGetTaskAssignableUsers } from '@core/react-query/features/tasks/task'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'
import { User } from '@core/entities/user'

export type TaskAssignUserPickerProps = Omit<TextFieldProps, 'onChange'> & {
  projectId: number
  templateId: number
  stepId: string
  value: TaskAssignableUser | null
  onChange?: (user: TaskAssignableUser) => void
  currentUser?: TaskAssignableUser | User
}

const TaskAssignUserPicker: React.FC<TaskAssignUserPickerProps> = ({
  projectId,
  templateId,
  stepId,
  value,
  onChange,
  currentUser,
  ...props
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  const { data: assignableUsersResponse } = useGetTaskAssignableUsers({
    projectId,
    templateId,
    stepId,
    term: searchTerm,
  })

  const assignableUsers =
    assignableUsersResponse?.pages
      .flatMap((page) => page.content)
      .filter((user) => user.id !== currentUser?.id) ?? []

  return (
    <Autocomplete
      value={value?.id}
      options={assignableUsers.map((user) => user.id)}
      getOptionLabel={(optionId) => {
        const user = assignableUsers.find((u) => u.id === optionId)
        return user ? user?.getFullName() : ''
      }}
      renderOption={(props, optionId, { index }) => {
        const user = assignableUsers.find((u) => u.id === optionId)
        return (
          <li {...props} data-test={`select-assignee-dropdown-option-${index}`}>
            <Box>
              <Text>{user ? `${user.firstName} ${user.lastName}` : ''}</Text>
              <Text fontSize={15} color='grey'>
                {user?.email}
              </Text>
            </Box>
          </li>
        )
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setSearchTerm(newInputValue)
        }
      }}
      onChange={(_, newValue) => {
        const user = assignableUsers.find((u) => u.id === newValue)
        if (user && onChange) {
          onChange(user)
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={localizedStrings.assignTo}
          data-test='select-assignee-dropdown-btn'
          placeholder={localizedStrings.selectUser}
          required
          fullWidth
          {...props}
        />
      )}
      disableClearable
    />
  )
}

export { TaskAssignUserPicker }
